import React, { useMemo, Suspense, useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  useTheme as useMuiTheme,
  alpha,
  IconButton,
  Tooltip,
  Skeleton,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useCloseoutQueriesStore } from '../../hooks/useCloseoutQueriesStore';
import { DEFAULT_CLOSEOUT_TYPE_OPTIONS } from '../settings/CloseoutTypes';

import { useTheme } from '../../contexts/ThemeContext';

// Material icons
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Tab Components
import { ExtentionHeader } from './extension/ExtentionHeader';
import { DirectLaborHeader } from './DirectLaborHeader';
import { LaborFactoringHeader } from './LaborFactoringHeader';
import { IncidentalLaborHeader } from './IncidentalLaborHeader';
import { IndirectLaborHeader } from './IndirectLaborHeader';
import { EquipmentHeader } from './EquipmentHeader';
import { GeneralExpenseHeader } from './GeneralExpenseHeader';
import { SubcontractHeader } from './SubcontractHeader';
import { QuoteHeader } from './QuoteHeader';
import { TotalPricingHeader } from './TotalPricingHeader';
import { ScheduleOfValues } from './ScheduleOfValues';
import { AllocationTab } from './AllocationTab';

// Import responsive utilities
import { useResponsive, ResponsiveHelpers } from '../../theme';
import { CloseoutSettings } from '../settings/CloseoutSettings';

// Import Reports components
import { ReportsDialog } from '../reports/ReportsDialog';
import MaximizedTextEditor from '../reports/MaximizedTextEditor';
import { StreamingReportEditor } from '../reports/StreamingReportEditor';
import { useLockBid } from '../../utils/useLockBid';
import { ChangeOrderReportDialog } from '../reports/ChangeOrderReportDialog';

// Loading skeleton for Suspense fallback
const LoadingSkeleton = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  return (
    <Box sx={{ p: 2 }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={60}
        sx={{
          mb: 2,
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.primary.light, 0.1)
            : alpha(muiTheme.palette.primary.main, 0.1),
        }}
      />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={400}
        sx={{
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.primary.light, 0.1)
            : alpha(muiTheme.palette.primary.main, 0.1),
        }}
      />
    </Box>
  );
};

// Define an interface for the closeout tab data
interface CloseoutTabData {
  label: string;
  component: React.ReactNode;
}

export const CloseoutTabs = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();
  const responsive = useResponsive() as ResponsiveHelpers;
  const { isMobile } = responsive;
  const { lockBid } = useLockBid();

  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const setIsViewingExtension = useStore(useUnityBuildStore).setIsViewingExtension;
  const isViewingAllocation = useStore(useUnityBuildStore).isViewingAllocation;
  const setIsViewingAllocation = useStore(useUnityBuildStore).setIsViewingAllocation;

  // Add loading states
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isViewingCloseoutSettings, setIsViewingCloseoutSettings] = React.useState(false);
  const [isAllocationLoading, setIsAllocationLoading] = React.useState(false);
  const [showAllocationTab, setShowAllocationTab] = React.useState(false);
  const [isCloseoutSettingsLoading, setIsCloseoutSettingsLoading] = React.useState(false);
  const [showCloseoutSettings, setShowCloseoutSettings] = React.useState(false);

  // Reports state
  const [isReportsDialogOpen, setIsReportsDialogOpen] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorContent, setEditorContent] = React.useState('');
  const [editorTitle, setEditorTitle] = React.useState('');
  const [streamingEditorOpen, setStreamingEditorOpen] = React.useState(false);
  const [streamingReportId, setStreamingReportId] = React.useState('');
  const [streamingReportTitle, setStreamingReportTitle] = React.useState('');

  // Get project and estimate data for streaming reports
  const selectedProject = useStore(useUnityBuildStore).selectedProject;

  // Add state for change order report dialog
  const [changeOrderReportOpen, setChangeOrderReportOpen] = useState(false);

  // Add effect to listen for change order report events
  useEffect(() => {
    const handleOpenChangeOrderDialog = (event: CustomEvent) => {
      setChangeOrderReportOpen(true);
    };

    window.addEventListener('openChangeOrderDialog', handleOpenChangeOrderDialog as EventListener);

    return () => {
      window.removeEventListener(
        'openChangeOrderDialog',
        handleOpenChangeOrderDialog as EventListener
      );
    };
  }, []);

  // Function to handle opening the allocation dialog with loading state
  const handleOpenAllocation = () => {
    setIsAllocationLoading(true);
    setShowAllocationTab(false);
    setIsViewingAllocation(true);

    // Add a small delay before showing the actual component to ensure loading dialog appears first
    setTimeout(() => {
      setShowAllocationTab(true);
      // The allocation component will handle hiding the loader when done
    }, 100);
  };

  // Function to handle closing the allocation dialog
  const handleCloseAllocation = () => {
    setIsViewingAllocation(false);
    setShowAllocationTab(false);
    setIsAllocationLoading(false);
  };

  // Function to map active tab to allocation tab index
  const getJumpToTabIndex = (activeTabIndex: number): number | undefined => {
    const tabToAllocationMap: Record<number, number> = {
      2: 0, // Labor Factoring (index 2 in closeoutTabs) → index 0 in allocation tabs
      3: 1, // Incidental Labor → index 1
      4: 2, // Indirect Labor → index 2
      5: 3, // Equipment → index 3
      6: 4, // General Expenses → index 4
      7: 5, // Subcontracts → index 5
      8: 6, // Quotes → index 6
    };
    return tabToAllocationMap[activeTabIndex];
  };

  // Use our optimized query store with React Query
  const isEstimate = Boolean(selectedEstimate?.estimateid);
  const { activeTab, setActiveTab, getActiveHook, prefetchTabData } = useCloseoutQueriesStore(
    isEstimate,
    selectedEstimate?.estimateid
  );

  // Get the active hook for the current tab
  const activeHook = getActiveHook();

  // Memoized list of tabs
  const closeoutTabs = useMemo<CloseoutTabData[]>(
    () => [
      {
        label: 'Extended Costs',
        component: <ExtentionHeader refetchData={activeHook?.refetch} />,
      },
      {
        label: 'Direct Labor',
        component: <DirectLaborHeader />,
      },
      {
        label: 'Labor Factoring',
        component: <LaborFactoringHeader />,
      },
      {
        label: 'Incidental Labor',
        component: <IncidentalLaborHeader />,
      },
      {
        label: 'Indirect Labor',
        component: <IndirectLaborHeader />,
      },
      {
        label: 'Equipment',
        component: <EquipmentHeader />,
      },
      {
        label: 'General Expenses',
        component: <GeneralExpenseHeader />,
      },
      {
        label: 'Subcontracts',
        component: <SubcontractHeader />,
      },
      {
        label: 'Quotes',
        component: <QuoteHeader />,
      },
      {
        label: 'Total Pricing',
        component: <TotalPricingHeader />,
      },
      {
        label: 'Schedule of Values',
        component: <ScheduleOfValues />,
      },
    ],
    [activeHook]
  );

  // Handle tab switching
  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);

    // The activeHook will automatically update when activeTab changes
    if (activeHook?.refetch) {
      activeHook.refetch();
    }
  };

  // Handle tab hover for prefetching
  const handleTabHover = (tabIndex: number) => {
    prefetchTabData(tabIndex);
  };

  // Handle refreshing data with loading dialog
  const handleRefresh = async () => {
    if (!activeHook?.refetch) return;

    setIsRefreshing(true);

    try {
      // For Extended Costs tab, set the global refresh flag
      if (activeTab === 0) {
        (window as any).extensionRefreshFlag = true;
      }

      // Perform the refresh operation
      await activeHook.refetch();
    } finally {
      // Clean up and reset states
      if (activeTab === 0) {
        (window as any).extensionRefreshFlag = false;
      }
      setIsRefreshing(false);
    }
  };

  // Function to handle opening the closeout settings with loading state
  const handleOpenCloseoutSettings = () => {
    setIsCloseoutSettingsLoading(true);
    setShowCloseoutSettings(false);
    setIsViewingCloseoutSettings(true);

    // Add a small delay before showing the actual component to ensure loading dialog appears first
    setTimeout(() => {
      setShowCloseoutSettings(true);
      // The CloseoutSettings component will handle hiding the loader when done
    }, 100);
  };

  // Function to handle closing the closeout settings
  const handleCloseCloseoutSettings = () => {
    setIsViewingCloseoutSettings(false);
    setShowCloseoutSettings(false);
    setIsCloseoutSettingsLoading(false);
  };

  // Reports handlers
  const handleOpenEditor = React.useCallback((title: string, content: string) => {
    setEditorTitle(title);
    setEditorContent(content);
    setEditorOpen(true);
  }, []);

  const handleEditorClose = React.useCallback(() => {
    setEditorOpen(false);
    setEditorContent('');
    setEditorTitle('');
  }, []);

  const handleEditorChange = React.useCallback((value: string) => {
    setEditorContent(value);
  }, []);

  const handleOpenStreamingEditor = React.useCallback((title: string, reportId: string) => {
    setStreamingReportTitle(title);
    setStreamingReportId(reportId);
    setStreamingEditorOpen(true);
  }, []);

  const handleStreamingEditorClose = React.useCallback(() => {
    setStreamingEditorOpen(false);
    setStreamingReportId('');
    setStreamingReportTitle('');
  }, []);

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '100%',
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.background.paper, 0.9)
            : muiTheme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: isMobile ? 0 : 1,
        }}
      >
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Closeout sections tabs"
          sx={{
            flexShrink: 0, // Prevent tabs from shrinking
            borderBottom: 1,
            borderColor: alpha(muiTheme.palette.divider, 0.1),
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.default, 0.3)
              : alpha(muiTheme.palette.background.default, 0.4),
            '& .MuiTab-root': {
              color: muiTheme.palette.text.primary,
              textTransform: 'none',
              fontWeight: 500,
              minHeight: isMobile ? 40 : 48,
              padding: isMobile ? '6px 10px' : '12px 16px',
              transition: 'all 0.2s',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              '&.Mui-selected': {
                color: darkMode ? muiTheme.palette.primary.light : muiTheme.palette.primary.main,
                fontWeight: 600,
              },
              '&:hover': {
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.primary.main, 0.1)
                  : alpha(muiTheme.palette.primary.main, 0.05),
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: darkMode
                ? muiTheme.palette.primary.light
                : muiTheme.palette.primary.main,
              height: 3,
            },
            '& .MuiTabs-scrollButtons': {
              color: muiTheme.palette.text.secondary,
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
            '@media (max-width: 600px)': {
              '& .MuiTab-root': {
                minWidth: isMobile ? 80 : 100,
              },
            },
          }}
        >
          {closeoutTabs.map((item, index) => (
            <Tab
              key={index}
              label={isMobile ? item.label.split(' ')[0] : item.label} // Show only first word on mobile
              id={`closeout-tab-${index}`}
              aria-controls={`closeout-tabpanel-${index}`}
              onMouseEnter={() => handleTabHover(index)}
            />
          ))}
        </Tabs>

        {/* Main Content with Suspense */}
        <Box
          role="tabpanel"
          id={`closeout-tabpanel-${activeTab}`}
          aria-labelledby={`closeout-tab-${activeTab}`}
          sx={{
            flex: 1,
            minHeight: 0, // This is critical for a flex child to properly scroll
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            p: { xs: 1, sm: 2 },
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain',
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.background.paper, 0.05)
                : alpha(muiTheme.palette.background.paper, 0.1),
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.primary.light, 0.2)
                : alpha(muiTheme.palette.primary.main, 0.2),
              '&:hover': {
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.primary.light, 0.4)
                  : alpha(muiTheme.palette.primary.main, 0.3),
              },
            },
            '@media (pointer: fine)': {
              scrollbarWidth: 'thin',
              msOverflowStyle: 'auto',
            },
            WebkitOverflowScrolling: 'touch',
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.default, 0.1)
              : alpha(muiTheme.palette.background.default, 0.2),
          }}
        >
          <Suspense fallback={<LoadingSkeleton />}>{closeoutTabs[activeTab].component}</Suspense>
        </Box>

        {/* Controls */}
        <Box
          sx={{
            p: { xs: 1, sm: 1.5 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            borderTop: 1,
            flexShrink: 0, // Prevent controls from shrinking
            borderColor: alpha(muiTheme.palette.divider, 0.1),
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.default, 0.3)
              : alpha(muiTheme.palette.background.default, 0.4),
          }}
        >
          <Button
            onClick={() => setIsViewingExtension(false)}
            variant="contained"
            color="secondary"
            startIcon={!isMobile && <CloseIcon />}
            size={isMobile ? 'small' : 'medium'}
            aria-label="Close window"
            sx={{
              fontSize: isMobile ? '0.75rem' : undefined,
              padding: isMobile ? '6px 8px' : undefined,
            }}
          >
            {isMobile ? 'Close' : 'Close Window'}
          </Button>

          <Button
            onClick={handleOpenCloseoutSettings}
            variant="contained"
            color="primary"
            size={isMobile ? 'small' : 'medium'}
            aria-label="Close window"
            sx={{
              fontSize: isMobile ? '0.75rem' : undefined,
              padding: isMobile ? '6px 8px' : undefined,
            }}
          >
            {isMobile ? 'Closeouts' : 'Closeout Settings'}
          </Button>

          <Button
            onClick={handleOpenAllocation}
            variant="contained"
            color="primary"
            startIcon={!isMobile && <AssignmentIcon />}
            size={isMobile ? 'small' : 'medium'}
            aria-label="View allocations"
            sx={{
              fontSize: isMobile ? '0.75rem' : undefined,
              padding: isMobile ? '6px 8px' : undefined,
            }}
          >
            {isMobile ? 'Alloc.' : 'Allocations'}
          </Button>

          <Button
            onClick={() => setIsReportsDialogOpen(true)}
            variant="contained"
            color="primary"
            startIcon={!isMobile && <FileDownloadIcon />}
            size={isMobile ? 'small' : 'medium'}
            aria-label="Generate reports"
            sx={{
              fontSize: isMobile ? '0.75rem' : undefined,
              padding: isMobile ? '6px 8px' : undefined,
            }}
          >
            {isMobile ? 'Reports' : 'Reports'}
          </Button>

          {/* Refresh Button to Manually Fetch Data */}
          {activeHook?.refetch && (
            <Tooltip title={`Refresh ${closeoutTabs[activeTab].label} data`} arrow>
              <span>
                <Button
                  onClick={handleRefresh}
                  variant="outlined"
                  color="primary"
                  startIcon={!isMobile && <RefreshIcon />}
                  size={isMobile ? 'small' : 'medium'}
                  aria-label={`Refresh ${closeoutTabs[activeTab].label} data`}
                  disabled={isRefreshing || lockBid()}
                  sx={{
                    fontSize: isMobile ? '0.75rem' : undefined,
                    padding: isMobile ? '6px 8px' : undefined,
                    minWidth: isMobile ? '32px' : undefined,
                    ...(isMobile && {
                      '& .MuiButton-startIcon': { margin: 0 },
                    }),
                  }}
                >
                  {isMobile ? (
                    isRefreshing ? (
                      <CircularProgress size={16} color="primary" />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )
                  ) : isRefreshing ? (
                    'Refreshing...'
                  ) : (
                    'Refresh'
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* Allocation Dialog */}
      <Dialog
        open={isViewingAllocation}
        onClose={handleCloseAllocation}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 5,
          sx: {
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.paper, 0.9)
              : muiTheme.palette.background.paper,
            borderRadius: isMobile ? 0 : 1,
            overflow: 'hidden',
            height: isMobile ? '100%' : 'auto',
            m: isMobile ? 0 : 2,
          },
        }}
        aria-labelledby="allocation-dialog-title"
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 1,
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.default, 0.3)
              : alpha(muiTheme.palette.background.default, 0.4),
            borderBottom: 1,
            borderColor: alpha(muiTheme.palette.divider, 0.1),
          }}
        >
          <Tooltip title="Close allocations" arrow>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseAllocation}
              aria-label="Close allocations dialog"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <DialogContent
          sx={{
            p: { xs: 1, sm: 2 },
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain',
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.background.paper, 0.05)
                : alpha(muiTheme.palette.background.paper, 0.1),
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.primary.light, 0.2)
                : alpha(muiTheme.palette.primary.main, 0.2),
              '&:hover': {
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.primary.light, 0.4)
                  : alpha(muiTheme.palette.primary.main, 0.3),
              },
            },
            '@media (pointer: fine)': {
              scrollbarWidth: 'thin',
              msOverflowStyle: 'auto',
            },
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {isAllocationLoading && !showAllocationTab && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
              <CircularProgress size={48} color="primary" sx={{ mb: 2 }} />
              <Typography variant="h6" align="center">
                Loading allocation data...
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                This may take a few moments
              </Typography>
            </Box>
          )}

          {showAllocationTab && (
            <AllocationTab
              jumpto={getJumpToTabIndex(activeTab)}
              onLoaded={() => setIsAllocationLoading(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Closeout Settings Dialog */}
      <Dialog
        open={isViewingCloseoutSettings}
        onClose={handleCloseCloseoutSettings}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 5,
          sx: {
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.paper, 0.9)
              : muiTheme.palette.background.paper,
            borderRadius: isMobile ? 0 : 1,
            overflow: 'hidden',
            height: isMobile ? '100%' : 'auto',
            m: isMobile ? 0 : 2,
          },
        }}
      >
        {isCloseoutSettingsLoading && !showCloseoutSettings && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
            <CircularProgress size={48} color="primary" sx={{ mb: 2 }} />
            <Typography variant="h6" align="center">
              Loading closeout settings...
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
              This may take a few moments
            </Typography>
          </Box>
        )}

        {showCloseoutSettings && (
          <CloseoutSettings
            handleCloseWindow={handleCloseCloseoutSettings}
            jumpto={(() => {
              // Get the current tab label
              const currentTabLabel = closeoutTabs[activeTab]?.label;

              // Find matching closeout type option based on label
              const matchingOption = DEFAULT_CLOSEOUT_TYPE_OPTIONS.find(
                option => option.label === currentTabLabel
              );

              // Return the matching key or default to directlaborList
              return matchingOption?.key || 'directlaborList';
            })()}
            onLoaded={() => setIsCloseoutSettingsLoading(false)}
          />
        )}
      </Dialog>

      {/* Refreshing Dialog */}
      <Dialog
        open={isRefreshing}
        PaperProps={{
          elevation: 5,
          sx: {
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.paper, 0.9)
              : muiTheme.palette.background.paper,
            borderRadius: 2,
            p: 3,
            minWidth: { xs: '80%', sm: 300 },
            maxWidth: { xs: '90%', sm: 400 },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
          <CircularProgress size={48} color="primary" sx={{ mb: 2 }} />
          <Typography variant="h6" align="center">
            Recalculating values for closeout {closeoutTabs[activeTab].label}
          </Typography>
        </Box>
      </Dialog>

      {/* Reports Dialog */}
      <ReportsDialog
        open={isReportsDialogOpen}
        onClose={() => setIsReportsDialogOpen(false)}
        onOpenEditor={handleOpenEditor}
        onOpenStreamingEditor={handleOpenStreamingEditor}
        projectId={selectedProject?.id}
        estimateId={selectedEstimate?.estimateid}
        estimateName={selectedEstimate?.name}
        onOpenChangeOrderReport={() => setChangeOrderReportOpen(true)}
      />

      {/* Report Editor */}
      <MaximizedTextEditor
        open={editorOpen}
        onClose={handleEditorClose}
        value={editorContent}
        onChange={handleEditorChange}
        title={editorTitle}
        placeholder="Your report content will appear here..."
        doneButtonText="Close Report"
        enableTemplates={false}
      />

      {/* Streaming Report Editor */}
      <StreamingReportEditor
        open={streamingEditorOpen}
        onClose={handleStreamingEditorClose}
        title={streamingReportTitle}
        reportId={streamingReportId}
        projectId={selectedProject?.id || ''}
        estimateId={selectedEstimate?.estimateid || ''}
        estimateName={selectedEstimate?.name || ''}
        placeholder="AI-generated report content will appear here..."
        doneButtonText="Close Report"
        enableTemplates={true}
        suggestedTemplateType="general"
      />

      {/* Add the ChangeOrderReportDialog */}
      <ChangeOrderReportDialog
        open={changeOrderReportOpen}
        onClose={() => setChangeOrderReportOpen(false)}
      />
    </>
  );
};
